import React, { useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { pricingPlans } from '../mock';
import { useToast } from '../hooks/use-toast';

const PricingModal = ({ open, onClose }) => {
  const { toast } = useToast();

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSubscribe = (plan) => {
    if (plan.checkoutUrl) {
      window.location.href = plan.checkoutUrl;
    }
  };

  const ctaClasses = (style) => {
    if (style === 'solid') return 'bg-gradient-to-b from-[#e8c766] to-[#d4af37] text-[#1a1500] hover:from-[#f0d27a] hover:to-[#e0b84c]';
    if (style === 'outline-gold') return 'bg-transparent text-[#e0b84c] border border-[#c99a2e]/50 hover:bg-[#c99a2e]/10';
    return 'bg-transparent text-white border border-[#3a3a45] hover:border-[#c99a2e]/50';
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <button onClick={onClose} className="fixed top-5 right-5 h-10 w-10 rounded-full bg-[#15151d] border border-[#2a2a33] flex items-center justify-center text-[#9a9aa4] hover:text-white transition-colors z-10">
        <X className="h-5 w-5" />
      </button>

      <div className="relative min-h-full flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-6xl animate-fade-up">
          <div className="text-center mb-10">
            <h2 className="font-serif-display text-[34px] sm:text-[42px] text-white">
              Unlock <span className="gold-gradient-text">PACE MASTER</span> Premium
            </h2>
            <p className="mt-3 text-[15px] text-[#9a9aa4] max-w-xl mx-auto">
              Gain full unlimited access to live courtroom pacing calculations, advanced metrics, and custom diagnostics.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-7 bg-[#0e0e15] transition-all ${
                  plan.highlight
                    ? 'border border-[#c99a2e]/60 gold-glow lg:-translate-y-3'
                    : 'border border-[#22222b]'
                }`}
              >
                {plan.badge && (
                  <div
                    className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10.5px] font-semibold whitespace-nowrap ${
                      plan.highlight
                        ? 'bg-gradient-to-b from-[#e8c766] to-[#d4af37] text-[#1a1500]'
                        : 'bg-[#15151d] text-[#c99a2e] border border-[#c99a2e]/40'
                    }`}
                  >
                    {plan.badge}
                  </div>
                )}

                <h3 className={`font-serif-display text-[22px] ${plan.highlight ? 'text-[#e0b84c]' : 'text-white'}`}>{plan.name}</h3>

                <div className="mt-4 flex items-end gap-1">
                  <span className="text-[42px] font-bold text-white leading-none">{plan.price}</span>
                  <span className="text-[14px] text-[#8a8a94] mb-1">{plan.unit}</span>
                </div>
                {plan.sub && <p className="mt-2 text-[13px] font-semibold text-[#c99a2e]">{plan.sub}</p>}
                {!plan.sub && <div className="mt-2 h-[19px]" />}

                <ul className="mt-6 space-y-3">
                  {plan.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2.5 text-[13px] text-[#b8b8c0]">
                      <Check className="h-4 w-4 mt-0.5 text-[#c99a2e] shrink-0" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan)}
                  className={`mt-7 w-full py-3 rounded-lg font-semibold text-[14px] transition-all ${ctaClasses(plan.ctaStyle)}`}
                >
                  {plan.cta}
                </button>
                <p className="text-center text-[11px] text-[#5a5a64] mt-2">Secured by Stripe</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-[12.5px] text-[#7c7c86]">Subscriptions are processed instantly. Secure payments managed and encrypted by Stripe. Cancel online at any time with a single click.</p>
            <button
              onClick={() => toast({ title: 'Restore purchase', description: 'Purchase restore is not available in this preview build.' })}
              className="mt-3 text-[12.5px] text-[#e0b84c] font-medium underline hover:text-[#f0d27a] transition-colors"
            >
              Already subscribed? Click here to restore your purchase
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
